
import { AppConst, UtilsConst } from '@/constants'
import { getInstance } from '@/plugins/auth0'
import Axios from 'axios'
import { Component, Prop, Vue } from 'vue-property-decorator'

@Component({})
export default class FilePreviewVue extends Vue {
  // Props-first API supports the tenant settings modal (in-page rendering).
  // Route query/params fallback keeps this component reusable for standalone
  // preview routes without duplicating preview logic.
  @Prop({ type: Number, default: 0 })
  private referenceId!: number

  @Prop({ type: String, default: '' })
  private requestPath!: string

  @Prop({ type: String, default: 'document' })
  private filename!: string

  @Prop({ type: String, default: '' })
  private contentType!: string

  @Prop({ type: Number, default: -1 })
  private tenantId!: number

  private loading: boolean = true
  private errorMessage: string = ''
  private blobUrl: string = ''
  private blobMimeType: string = ''
  private isValidatedPdfBlob: boolean = false
  private previewAbortController: AbortController | null = null

  private get resolvedFilename(): string {
    if (this.filename && this.filename.length > 0) {
      return this.filename
    }

    const queryFilename = this.$route.query.filename
    return typeof queryFilename === 'string' && queryFilename.length > 0 ? queryFilename : 'document'
  }

  private get resolvedTenantId(): number {
    if (this.tenantId > 0) {
      return this.tenantId
    }

    const queryTenantId = this.$route.query.tenantId
    const parsedTenantId = parseInt(`${queryTenantId || ''}`, 10)
    return Number.isNaN(parsedTenantId) ? -1 : parsedTenantId
  }

  private get resolvedReferenceId(): string {
    if (this.referenceId > 0) {
      return `${this.referenceId}`
    }

    return `${this.$route.params.id || ''}`
  }

  private get resolvedRequestPath(): string {
    if (this.requestPath) {
      return this.requestPath
    }

    if (this.resolvedReferenceId) {
      return `/asset/v1/references/${this.resolvedReferenceId}/download-url`
    }

    const queryDownloadUrl = this.$route.query.downloadUrl
    if (typeof queryDownloadUrl === 'string' && queryDownloadUrl.length > 0) {
      return queryDownloadUrl
    }

    return ''
  }

  private get requestUrl(): string {
    return /^https?:\/\//i.test(this.resolvedRequestPath)
      ? this.resolvedRequestPath
      : `${AppConst.apiDomain}${this.resolvedRequestPath}`
  }

  private get isPdfPreview(): boolean {
    if (!this.blobUrl) {
      return false
    }

    if (this.blobMimeType.includes('text/html')) {
      return false
    }

    // Render iframe preview only for blobs that pass PDF signature validation.
    return this.isValidatedPdfBlob
  }

  protected async mounted() {
    await this.loadPreview()
  }

  protected beforeDestroy() {
    this.cancelPendingPreviewRequest()
    this.revokeBlobUrl()
  }

  private cancelPendingPreviewRequest() {
    if (this.previewAbortController != null) {
      this.previewAbortController.abort()
      this.previewAbortController = null
    }
  }

  private revokeBlobUrl() {
    if (this.blobUrl) {
      URL.revokeObjectURL(this.blobUrl)
      this.blobUrl = ''
    }
    this.isValidatedPdfBlob = false
  }

  private async isPdfBlob(blob: Blob): Promise<boolean> {
    if (!blob || blob.size <= 0) {
      return false
    }

    try {
      // Lightweight guard: only render iframe for blobs that start with "%PDF-".
      const headerBuffer = await blob.slice(0, 5).arrayBuffer()
      const headerBytes = new Uint8Array(headerBuffer)

      let header = ''
      for (let i = 0; i < headerBytes.length; i++) {
        header += String.fromCharCode(headerBytes[i])
      }

      return header === '%PDF-'
    } catch (error) {
      return false
    }
  }

  private isTrustedRequestUrl(url: string): boolean {
    try {
      const requestUrl = new URL(url, AppConst.apiDomain)
      const apiOrigin = new URL(AppConst.apiDomain).origin
      // Prevent sending bearer token to arbitrary external origins.
      return requestUrl.origin === apiOrigin
    } catch (error) {
      return false
    }
  }

  private async loadPreview() {
    if (!this.resolvedReferenceId && !this.resolvedRequestPath) {
      this.errorMessage = 'Missing file reference.'
      this.loading = false
      return
    }

    const requestUrl = this.requestUrl
    if (!this.isTrustedRequestUrl(requestUrl)) {
      this.errorMessage = 'Invalid file reference.'
      this.loading = false
      return
    }

    try {
      this.cancelPendingPreviewRequest()
      this.previewAbortController = new AbortController()

      const authService: any = getInstance()
      const headers: Record<string, string> = {}

      // Prefer existing access token from the current app instance.
      // This avoids unnecessary silent-auth calls and is generally more stable
      // than iframe-driven token retrieval in strict privacy modes.
      const accessToken =
        (authService && authService.accessToken) ||
        (authService && authService.getAccessTokenSilently ? await authService.getAccessTokenSilently() : '')
      if (accessToken) {
        headers.Authorization = `Bearer ${accessToken}`
      }

      if (this.resolvedTenantId > 0) {
        headers[UtilsConst.RequestHeaders.CustomHeaderKeyTenantId] = `${this.resolvedTenantId}`
      }

      const response = await Axios.get(requestUrl, {
        headers,
        responseType: 'blob',
        signal: this.previewAbortController.signal,
      })

      this.revokeBlobUrl()
      this.blobUrl = URL.createObjectURL(response.data)
      this.blobMimeType = response.data && response.data.type ? `${response.data.type}`.toLowerCase() : ''
      this.isValidatedPdfBlob = await this.isPdfBlob(response.data)
    } catch (error) {
      if (Axios.isCancel(error) || (error as any).code === 'ERR_CANCELED') {
        return
      }
      this.errorMessage = 'Could not load this file preview.'
    } finally {
      this.previewAbortController = null
      this.loading = false
    }
  }
}
